<template>
  <section class="plus-recent-projects">
    <div class="plus-recent-projects__head">
      <h2 class="plus-recent-projects__title">最近项目</h2>
      <router-link to="/workspace" class="plus-recent-projects__all">
        全部项目
        <i class="fas fa-chevron-right text-[9px] opacity-70" aria-hidden="true"></i>
      </router-link>
    </div>

    <div class="plus-recent-projects__grid-wrap">
      <div v-if="loading" class="plus-recent-projects__row">
        <div
          v-for="i in 5"
          :key="'rp-sk-' + i"
          class="plus-recent-card plus-recent-card--skeleton animate-pulse"
        >
          <div class="plus-recent-card__cover bg-white/[0.06]"></div>
          <div class="plus-recent-card__foot space-y-2 px-3 py-2.5">
            <div class="h-3 w-16 rounded bg-white/[0.08]"></div>
            <div class="h-2.5 w-20 rounded bg-white/[0.06]"></div>
          </div>
        </div>
      </div>

      <div v-else class="plus-recent-projects__row">
        <button type="button" class="plus-recent-card plus-recent-card--create" @click="onStartCreate">
          <div class="plus-recent-card__create-main">
            <span class="plus-recent-card__create-icon" aria-hidden="true">
              <i class="fas fa-plus"></i>
            </span>
            <span class="plus-recent-card__create-text">开始创作</span>
          </div>
          <div class="plus-recent-card__create-tag">
            <i class="fas fa-chart-bar text-[9px] opacity-80" aria-hidden="true"></i>
            <span>Seedance2.0</span>
          </div>
        </button>

        <article
          v-for="p in displayProjects"
          :key="p.uuid"
          class="plus-recent-card plus-recent-card--project group"
        >
          <button type="button" class="plus-recent-card__open" @click="openProject(p)">
            <div class="plus-recent-card__cover">
              <img
                v-if="p.thumb_url"
                :src="p.thumb_url"
                alt=""
                loading="lazy"
                class="h-full w-full object-cover"
                @error="onThumbError(p)"
              />
              <div v-else class="plus-recent-card__cover-empty">
                <i class="far fa-image text-xl opacity-40"></i>
              </div>
            </div>
            <div class="plus-recent-card__foot">
              <div class="min-w-0 flex-1 text-left">
                <p class="plus-recent-card__name truncate">{{ p.title || '未命名' }}</p>
                <p class="plus-recent-card__date">{{ formatProjectDate(p.updated_at || p.created_at) }}</p>
              </div>
            </div>
          </button>
          <div class="plus-recent-card__menu-wrap">
            <button
              type="button"
              class="plus-recent-card__menu-btn"
              :class="{ 'is-open': openMenuUuid === p.uuid }"
              aria-label="更多操作"
              @click.stop="toggleMenu(p.uuid)"
            >
              <i class="fas fa-ellipsis-h text-[11px]"></i>
            </button>
            <div v-if="openMenuUuid === p.uuid" class="plus-recent-card__menu" @click.stop>
              <button type="button" class="plus-recent-card__menu-item" @click="openProject(p)">
                打开
              </button>
              <button
                type="button"
                class="plus-recent-card__menu-item plus-recent-card__menu-item--danger"
                @click="removeProject(p)"
              >
                删除
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>

    <p v-if="!loading && loadError" class="plus-recent-projects__error">{{ loadError }}</p>
  </section>
</template>

<script>
import { fetchNodeCanvasList, deleteNodeCanvas } from '@/utils/nodeCanvasApi'
import { showErrorToast, showSuccessToast } from '@/utils/toast'

/** 一行 5 格：1 个「开始创作」+ 最多 4 个项目 */
const DISPLAY_LIMIT = 4

export default {
  name: 'PlusRecentProjects',
  data() {
    return {
      loading: true,
      loadError: '',
      projects: [],
      openMenuUuid: ''
    }
  },
  computed: {
    displayProjects() {
      return (this.projects || []).slice(0, DISPLAY_LIMIT)
    }
  },
  mounted() {
    this.loadProjects()
    document.addEventListener('click', this.closeMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
  },
  methods: {
    async loadProjects() {
      this.loading = true
      this.loadError = ''
      try {
        const { rows, listError } = await fetchNodeCanvasList({ limit: 12 })
        this.projects = Array.isArray(rows)
          ? rows
              .filter(r => r && r.uuid)
              .sort((a, b) => {
                const ta = new Date(a.updated_at || a.created_at || 0).getTime()
                const tb = new Date(b.updated_at || b.created_at || 0).getTime()
                return tb - ta
              })
          : []
        if (listError && !this.projects.length) {
          this.loadError = listError
        }
      } catch (e) {
        this.projects = []
        this.loadError = (e && e.message) || '项目加载失败'
      } finally {
        this.loading = false
      }
    },
    formatProjectDate(raw) {
      if (raw == null || raw === '') return '—'
      const s = String(raw).trim()
      const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return s.slice(0, 10)
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`
    },
    onStartCreate() {
      this.$router.push({ path: '/workspace' }).catch(() => {})
    },
    openProject(p) {
      const uuid = p && p.uuid != null ? String(p.uuid).trim() : ''
      if (!uuid) return
      this.closeMenu()
      this.$router.push({ path: '/workspace', query: { canvas: uuid } }).catch(() => {})
    },
    toggleMenu(uuid) {
      const id = String(uuid || '')
      this.openMenuUuid = this.openMenuUuid === id ? '' : id
    },
    closeMenu() {
      this.openMenuUuid = ''
    },
    onThumbError(p) {
      if (p && p.thumb_url) this.$set(p, 'thumb_url', '')
    },
    async removeProject(p) {
      const uuid = p && p.uuid != null ? String(p.uuid).trim() : ''
      if (!uuid) return
      this.closeMenu()
      try {
        await deleteNodeCanvas(uuid)
        this.projects = this.projects.filter(item => String(item.uuid) !== uuid)
        showSuccessToast('已删除')
      } catch (e) {
        showErrorToast((e && e.message) || '删除失败')
      }
    }
  }
}
</script>

<style scoped>
.plus-recent-projects {
  margin-top: 4rem;
}
.plus-recent-projects__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
}
.plus-recent-projects__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #fff;
}
@media (min-width: 640px) {
  .plus-recent-projects__title {
    font-size: 1.25rem;
  }
}
.plus-recent-projects__all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.42);
  text-decoration: none;
  transition: color 0.15s ease;
}
.plus-recent-projects__all:hover {
  color: rgba(255, 255, 255, 0.72);
}
.plus-recent-projects__grid-wrap {
  width: 100%;
  overflow: visible;
}
.plus-recent-projects__row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  width: 100%;
}
@media (max-width: 1023px) {
  .plus-recent-projects__row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 639px) {
  .plus-recent-projects__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
}
.plus-recent-card {
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
}
.plus-recent-card--create {
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
  min-height: 128px;
  padding: 8px;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}
.plus-recent-card--create:hover {
  border-color: rgba(139, 92, 246, 0.35);
  background: rgba(124, 58, 237, 0.08);
}
.plus-recent-card__create-main {
  display: flex;
  flex: 1 1 auto;
  min-height: 68px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.22);
}
.plus-recent-card__create-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
}
.plus-recent-card__create-text {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
}
.plus-recent-card__create-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
}
.plus-recent-card--project {
  display: flex;
  flex-direction: column;
  min-height: 128px;
  background: rgba(18, 18, 22, 0.92);
}
.plus-recent-card__open {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
}
.plus-recent-card__cover {
  aspect-ratio: 2 / 1;
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
}
.plus-recent-card__cover-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.22);
  font-size: 14px;
}
.plus-recent-card__foot {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 7px 30px 7px 8px;
}
.plus-recent-card__name {
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.92);
}
.plus-recent-card__date {
  margin: 2px 0 0;
  font-size: 9px;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.38);
}
.plus-recent-card__menu-wrap {
  position: absolute;
  right: 4px;
  bottom: 7px;
}
.plus-recent-card__menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.42);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}
.plus-recent-card__menu-btn:hover,
.plus-recent-card__menu-btn.is-open {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.82);
}
.plus-recent-card__menu {
  position: absolute;
  right: 0;
  bottom: calc(100% + 4px);
  z-index: 20;
  min-width: 88px;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(22, 22, 28, 0.98);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}
.plus-recent-card__menu-item {
  display: block;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 7px;
  background: transparent;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.82);
  text-align: left;
  cursor: pointer;
}
.plus-recent-card__menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
.plus-recent-card__menu-item--danger {
  color: rgba(252, 165, 165, 0.95);
}
.plus-recent-projects__error {
  margin: 10px 0 0;
  font-size: 12px;
  color: rgba(252, 165, 165, 0.88);
}
</style>
